import React, { useMemo } from "react";
import DataTable from "@/components/Datatable/Datatable";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { useCampaignStatusData } from "../../hooks/useCampaignStatusData";
import { useCampaignStatusMetadata } from "../../hooks/useCampaignStatusMetadata";
import { useDownloadCsv } from "../../hooks/useDownloadCsv";
import { campaignStatusColumns } from "../CampaignStatusColumns/CampaignStatusColumns";
import CampaignStatusFilters from "../CampaignStatusFilters/CampaignStatusFilters";
import CampaignStatusExpandedRow from "../CampaignStatusExpandedRow/CampaignStatusExpandedRow";
import { WeeklyStatisticsChart } from "@/modules/administration/features/Stochastic/CampaignStatus/components/WeeklyStatisticsChart/WeeklyStatisticsChart";
import { MailVolumeTimelineCard } from "@/modules/administration/features/Stochastic/CampaignStatus/components/MailVolumeTimeline/MailVolumeTimelineCard";
import { CampaignStatusItem } from "../../api/fetchCampaignStatus/types";

const CampaignStatusPage: React.FC = () => {
  const {
    campaigns,
    loading,
    error,
    totalCount,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  } = useCampaignStatusData();

  const { metadata } = useCampaignStatusMetadata();
  const { loading: downloadingCsv, downloadCsv } = useDownloadCsv(filters);

  const columns = useMemo(() => campaignStatusColumns(), []);

  return (
    <MainPageWrapper error={error} title="Campaign Status">
      <div className="mb-6">
        <WeeklyStatisticsChart
          activeCampaignsCount={metadata?.activeCampaignsCount ?? 0}
          currentWeekData={metadata?.currentWeekData ?? null}
          weeklyBatchesData={metadata?.weeklyBatchesData ?? null}
        />
      </div>

      {metadata?.mailVolumeData && metadata?.currentWeekData && (
        <div className="mb-6">
          <MailVolumeTimelineCard
            currentWeekData={metadata.currentWeekData}
            mailVolumeData={metadata.mailVolumeData}
            mostRecentPostageFileUpdatedAt={
              metadata.mostRecentPostageFileUpdatedAt
            }
          />
        </div>
      )}

      <CampaignStatusFilters
        campaignStatuses={metadata?.campaignStatusesData ?? []}
        filters={filters}
        isDownloading={downloadingCsv}
        onDownloadCsv={downloadCsv}
        onFilterChange={handleFilterChange}
      />

      <DataTable<CampaignStatusItem>
        columns={columns}
        data={campaigns}
        error={error}
        expandable={true}
        loading={loading}
        noDataMessage="No campaigns found"
        onPageChange={(newPageIndex, newPageSize) =>
          handlePaginationChange({
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        renderExpandedRow={(campaign) => (
          <CampaignStatusExpandedRow campaign={campaign} />
        )}
        rowKeyExtractor={(row) => row.id.toString()}
        sorting={sorting}
        totalCount={totalCount}
      />
    </MainPageWrapper>
  );
};

export default CampaignStatusPage;
